/** Blog import (WordPress, Ghost, Medium; background job with progress) and export (Markdown zip, JSON). */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dropdown, Input, Modal, Progress, Select, Space, Typography, Upload, message } from 'antd';
import { DownloadOutlined, ImportOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import { QUIET, directUpload } from '../api';
import { toastErr, useSite } from '../common';

const { Text } = Typography;

export const ExportButton = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const go = async (format) => {
        try { await api.download('/posts/export', `posts.${format === 'json' ? 'json' : 'zip'}`, { format }); } catch (e) { toastErr(e, t('Download failed')); }
    };
    return (
        <Dropdown trigger={['click']} menu={{ items: [{ key: 'markdown', label: t('Markdown (zip)') }, { key: 'json', label: 'JSON' }], onClick: ({ key }) => go(key) }}>
            <Button icon={<DownloadOutlined />}>{t('Export')}</Button>
        </Dropdown>
    );
};

export const ImportButton = ({ onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [source, setSource] = useState('auto');
    const [oldUrl, setOldUrl] = useState('');
    const [job, setJob] = useState(null);
    const [sending, setSending] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    const poll = (id) => {
        timer.current = setTimeout(async () => {
            let j = null;
            try { j = await api.get(`/posts/import/${id}`, null, QUIET); } catch (_) { /* keep polling */ }
            if (j) setJob(j);
            if (!j || ['queued', 'running'].includes(j.status)) poll(id);
            else if (j.status === 'done') onDone?.();
        }, 1500);
    };
    const start = async () => {
        setSending(true);
        try {
            const { key } = await directUpload(api, file, 'import');
            const r = await api.post('/posts/import', { key, name: file.name, source, old_site_url: oldUrl });
            setJob({ id: r.job_id, status: 'queued' });
            poll(r.job_id);
        } catch (e) { toastErr(e, t('Upload failed')); } finally { setSending(false); }
    };
    const reset = () => { setJob(null); setFile(null); };
    const running = job && ['queued', 'running'].includes(job.status);
    const pr = job?.progress;
    const r = job?.result;
    return (
        <>
            <Button icon={<ImportOutlined />} onClick={() => setOpen(true)}>{t('Import')}</Button>
            <Modal open={open} title={t('Import posts')} width={560} destroyOnHidden onCancel={() => { setOpen(false); if (!running) reset(); }} footer={job ? (
                <Button disabled={running} onClick={() => { setOpen(false); reset(); }}>{t('Close')}</Button>
            ) : (
                <Button type="primary" disabled={!file} loading={sending} onClick={start}>{t('Start import')}</Button>
            )}>
                {!job && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Upload.Dragger accept=".xml,.json,.zip" maxCount={1} beforeUpload={(f) => {
                            if (f.size > 100 * 1048576) { message.error(t('The file is larger than 100 MB')); return Upload.LIST_IGNORE; }
                            setFile(f);
                            return false;
                        }} onRemove={() => setFile(null)}>
                            <p style={{ fontSize: 28, margin: 0 }}><InboxOutlined /></p>
                            <p style={{ margin: 0 }}>{t('WordPress XML, Ghost JSON or Medium zip')}</p>
                        </Upload.Dragger>
                        <Select value={source} onChange={setSource} style={{ width: '100%' }} options={[
                            { value: 'auto', label: t('Detect automatically') }, { value: 'wordpress', label: 'WordPress' }, { value: 'ghost', label: 'Ghost' }, { value: 'medium', label: 'Medium' },
                        ]} />
                        {source === 'ghost' && <Input value={oldUrl} placeholder={t('Old site URL, to copy its images')} onChange={(e) => setOldUrl(e.target.value)} />}
                    </Space>
                )}
                {job && (
                    <div data-testid="import-state">
                        {running && <Progress percent={pr?.total ? Math.round((pr.done / pr.total) * 100) : 3} status="active" />}
                        {running && <Text type="secondary">{pr?.total ? t('{{done}} of {{total}} imported. You can close this window.', pr) : t('Reading the file…')}</Text>}
                        {job.status === 'failed' && <Alert type="error" showIcon message={job.error} />}
                        {job.status === 'done' && r && (
                            <Alert type="success" showIcon message={t('Import finished')}
                                description={t('{{posts}} posts, {{pages}} pages, {{terms}} categories, {{authors}} authors, {{media}} images, {{redirects}} redirects, {{skipped}} skipped. Publish the site to activate pages and redirects.', r)} />
                        )}
                        {job.status === 'done' && r?.warnings?.length > 0 && <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12 }}>{r.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
                    </div>
                )}
            </Modal>
        </>
    );
};
